import { supabaseClient } from './supabaseClient';

// Payment proof upload/view (Stage 23). One private screenshot per payment.
// Bucket + Storage RLS live server-side; reads are gated by can_view_payment_proof.
const BUCKET = 'payment-proofs';
const MAX_BYTES = 5 * 1024 * 1024; // 5MB (mirrors the bucket's file_size_limit)
const MIME_EXT = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };

export const PROOF_ACCEPT = 'image/jpeg,image/png,image/webp';

// Client-side validation (the bucket enforces the same server-side). { ok } | { error }.
export function validateProofFile(file) {
  if (!file) return { ok: true };
  if (!MIME_EXT[file.type]) return { error: 'Proof must be a JPG, PNG, or WebP image.' };
  if (file.size > MAX_BYTES) return { error: 'Proof image must be 5MB or smaller.' };
  return { ok: true };
}

// Upload one proof to <groupId>/<roundId>/<authUid>/<uuid>.<ext> and return the
// stored path. Throws on failure — the caller surfaces it inline (never silent).
export async function uploadPaymentProof({ file, groupId, roundId }) {
  const { data: { user } } = await supabaseClient.auth.getUser();
  if (!user) throw new Error('Not signed in.');
  const ext = MIME_EXT[file.type];
  if (!ext) throw new Error('Unsupported image type.');
  const name = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const path = `${groupId}/${roundId}/${user.id}/${name}.${ext}`;
  const { error } = await supabaseClient.storage
    .from(BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false });
  if (error) throw error;
  return path;
}

// A short-lived (60s) signed URL for viewing a proof. Returns null if there is no
// proof or the caller isn't permitted (the scoped-read policy denies it).
export async function getProofUrl(proofPath) {
  if (!proofPath) return null;
  const { data, error } = await supabaseClient.storage
    .from(BUCKET)
    .createSignedUrl(proofPath, 60);
  if (error) return null;
  return data?.signedUrl || null;
}
